import { useCallback, useEffect, useRef, useState } from "react";

import { listChatGeneratedArtifacts } from "./api";
import type { AttachmentKind, GeneratedArtifactPicker } from "./types";

/** Размер одной страницы ленты (потолок сервера — 500). */
const PAGE_SIZE = 100;
/** За сколько слайдов до края загруженного окна подтягивать соседнюю страницу. */
const PREFETCH_MARGIN = 10;

export interface ChatArtifactFeed {
    items: GeneratedArtifactPicker[];
    /** Сколько объектов ленты стоит перед `items[0]`. */
    offset: number;
    /** Размер всей ленты чата. */
    total: number;
}

export const EMPTY_CHAT_ARTIFACT_FEED: ChatArtifactFeed = { items: [], offset: 0, total: 0 };

interface UseChatArtifactFeedParams {
    chatId: string | undefined;
    enabled: boolean;
    /** Открыть ленту ВОКРУГ этого объекта; без него — с начала (самые новые). */
    anchorId?: string;
    type?: AttachmentKind;
}

/** Загруженное окно ленты: чьё оно и какие объекты в нём лежат. */
interface LoadedWindow {
    /** Чат и тип, для которых окно загружено. */
    key: string;
    ids: Set<string>;
}

/**
 * Нужен ли серверу новый запрос окна: своего окна ещё нет (другой чат или
 * первое открытие) либо якорь лежит за его пределами. Якорь из уже загруженного
 * окна перезапросом не лечится, а закрытие предпросмотра (якорь пропал) — тем
 * более: иначе вытянутая при открытии чата лента дёргалась бы заново на каждый
 * клик по артефакту.
 */
function needsWindow(loaded: LoadedWindow | null, key: string, anchorId: string | undefined): boolean {
    if (loaded?.key !== key) return true;
    return anchorId !== undefined && !loaded.ids.has(anchorId);
}

/**
 * Лента сгенерированных объектов чата, дочитываемая на любую длину.
 *
 * Сервер отдаёт окно ленты (вокруг `anchorId` либо с начала) и сообщает его
 * смещение и полный размер ленты; `onIndexChange` подтягивает соседние страницы,
 * когда листание подходит к краю окна. Поэтому длина чата ничем не ограничена —
 * ограничен только размер одной страницы.
 *
 * Окно тянется ОДИН раз на чат: якорь, уже лежащий в загруженном окне, нового
 * запроса не требует. Благодаря этому ленту можно включить заранее, при открытии
 * чата, — к моменту клика по артефакту она уже в руках, и счётчик со стрелками
 * появляются сразу, а не после ответа сервера, стоящего в очереди за тяжёлыми
 * медиа. Перезапрос делается только там, где старого окна не хватает: сменился
 * чат или тип, либо якорь лежит за пределами загруженного окна.
 */
export function useChatArtifactFeed({ chatId, enabled, anchorId, type }: UseChatArtifactFeedParams) {
    const [feed, setFeed] = useState<ChatArtifactFeed>(EMPTY_CHAT_ARTIFACT_FEED);
    // Курсоры, по которым догрузка уже сходила: без этого подход к краю окна
    // дёргал бы одну и ту же страницу на каждое движение стрелкой.
    const requested = useRef<Set<string>>(new Set());
    const loading = useRef(false);
    // Чей это загруженный кусок ленты и что в нём лежит — по нему решается,
    // нужен ли вообще новый запрос.
    const loaded = useRef<LoadedWindow | null>(null);

    useEffect(() => {
        if (!enabled || !chatId) {
            setFeed(EMPTY_CHAT_ARTIFACT_FEED);
            requested.current = new Set();
            loaded.current = null;
            return;
        }
        const key = `${chatId}:${type ?? "all"}`;
        const window = loaded.current;
        if (!needsWindow(window, key, anchorId)) return;
        // Окно чужого чата показывать нельзя даже мельком, а курсоры догрузки
        // привязаны к краям прежнего окна и к новому не относятся.
        if (window?.key !== key) setFeed(EMPTY_CHAT_ARTIFACT_FEED);
        requested.current = new Set();
        // Окно отмечается загруженным только по факту ответа: пометь его до
        // запроса — и отменённая попытка (перезапуск эффекта) оставила бы
        // хук в состоянии «окно этого чата уже есть», пустое и не
        // перезапрашиваемое, то есть без ленты до первого клика.
        let cancelled = false;
        void listChatGeneratedArtifacts(chatId, { limit: PAGE_SIZE, type, anchor: anchorId })
            .then((res) => {
                if (cancelled) return;
                setFeed({ items: res.artifacts, offset: res.offset, total: res.total });
                loaded.current = { key, ids: new Set(res.artifacts.map((a) => a.id)) };
            })
            .catch((err) => console.error("chat artifact feed: failed to load", err));
        return () => {
            cancelled = true;
        };
    }, [enabled, chatId, anchorId, type]);

    const loadMore = useCallback(
        async (direction: "before" | "after") => {
            const edge = direction === "before" ? feed.items.at(-1) : feed.items[0];
            if (!edge || !chatId || loading.current) return;
            const key = `${direction}:${edge.id}`;
            if (requested.current.has(key)) return;
            requested.current.add(key);
            loading.current = true;
            try {
                const res = await listChatGeneratedArtifacts(chatId, {
                    limit: PAGE_SIZE,
                    type,
                    [direction]: edge.id,
                });
                if (res.artifacts.length === 0) return;
                // Догруженные объекты — часть того же окна: клик по любому из
                // них не должен перезапрашивать ленту с нуля.
                for (const a of res.artifacts) loaded.current?.ids.add(a.id);
                setFeed((prev) => {
                    const known = new Set(prev.items.map((a) => a.id));
                    const fresh = res.artifacts.filter((a) => !known.has(a.id));
                    if (fresh.length === 0) return prev;
                    return direction === "before"
                        ? { ...prev, items: [...prev.items, ...fresh], total: res.total }
                        : { items: [...fresh, ...prev.items], offset: res.offset, total: res.total };
                });
            } catch (err) {
                console.error("chat artifact feed: failed to load more", err);
                requested.current.delete(key);
            } finally {
                loading.current = false;
            }
        },
        [chatId, type, feed.items],
    );

    /** Подтянуть соседей, когда листание подошло к краю загруженного окна. */
    const prefetchAround = useCallback(
        (index: number) => {
            if (feed.items.length === 0) return;
            // Есть ли ещё лента за краем окна — видно из offset/total, поэтому за
            // границами ленты догрузка не дёргается вовсе.
            const hasNewer = feed.offset > 0;
            const hasOlder = feed.offset + feed.items.length < feed.total;
            if (hasOlder && index >= feed.items.length - PREFETCH_MARGIN) void loadMore("before");
            if (hasNewer && index <= PREFETCH_MARGIN) void loadMore("after");
        },
        [feed, loadMore],
    );

    return { feed, prefetchAround };
}
